import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ActivityLog } from '@/components/ActivityLog';
import { BeneficiaryCombobox } from '@/components/common/BeneficiaryCombobox';
import { Camera, Loader2, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  project_id: '', job_id: '', estimate_id: '', item_index: null, item_description: '',
  expense_type: 'permit', beneficiary_type: 'other', beneficiary_id: '', beneficiary_name: '', beneficiary_contact_person: '',
  amount: '', quantity: 1, unit: 'unit', unit_price: '',
  description: '', date: '', notes: '',
  payment_status: 'unpaid', check_number: '', paid_date: '',
  payment_method: '', payment_reference: '',
  receipt_file_id: '', receipt_url: '',
};

// Shared create/edit dialog for an Expense. Used by ExpensesPage, ProjectDetailPage and JobDetailPage.
export const ExpenseDialog = ({ open, onClose, onSaved, editing = null, defaultProjectId = '', defaultJobId = '' }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY);
  const [types, setTypes] = useState(['permit', 'fuel', 'labor', 'material', 'subcontractor', 'other']);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null); // 'ok' | 'failed' | null
  // Fix P (2026-09-10) — Alian: "me hace falta poner una clausula" para no
  // duplicar beneficiarios. Solo AVISA (nunca bloquea) cuando el nombre
  // tipeado se parece a uno que ya existe.
  const [similarWarning, setSimilarWarning] = useState(null); // { typedName, match }
  const fileInputRef = useRef(null);

  // Load reference data once when the dialog opens.
  useEffect(() => {
    if (!open) return;
    (async () => {
      const [tp, pr, us, co] = await Promise.all([
        api.get('/expenses/types').catch(() => ({ data: { types: [] } })),
        api.get('/projects', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } })),
        api.get('/auth/users').catch(() => ({ data: [] })),
        api.get('/contacts', { params: { page_size: 500, sort_by: 'name' } }).catch(() => ({ data: { items: [] } })),
      ]);
      if (tp.data.types?.length) setTypes(tp.data.types);
      setProjects(pr.data.items || pr.data || []);
      setUsers(us.data || []);
      setContacts(co.data.items || co.data || []);
    })();
  }, [open]);

  // Initialize form on open / when editing target changes.
  useEffect(() => {
    if (!open) return;
    setScanResult(null);
    if (editing) {
      // Legacy expenses (before qty x unit price existed) only had `amount` —
      // backfill quantity=1 / unit_price=amount so the computed total matches.
      const qty = editing.quantity || 1;
      const unitPrice = (editing.unit_price !== undefined && editing.unit_price !== null && editing.unit_price !== 0)
        ? editing.unit_price
        : (editing.amount ?? '');
      setForm({ ...EMPTY, ...editing, amount: editing.amount ?? '', quantity: qty, unit: editing.unit || 'unit', unit_price: unitPrice });
    } else {
      setForm({ ...EMPTY, project_id: defaultProjectId || '', job_id: defaultJobId || '', date: new Date().toISOString().slice(0, 10) });
    }
  }, [open, editing, defaultProjectId, defaultJobId]);

  // Load the selected project's estimates (to assign an expense to an item).
  const loadEstimates = useCallback(async (projectId) => {
    if (!projectId) { setEstimates([]); return; }
    const res = await api.get('/estimates', { params: { page_size: 500 } }).catch(() => ({ data: { items: [] } }));
    setEstimates((res.data.items || res.data || []).filter(e => e.project_id === projectId));
  }, []);

  useEffect(() => { if (open && form.project_id) loadEstimates(form.project_id); }, [open, form.project_id, loadEstimates]);

  const selectedEstimate = estimates.find(e => e.id === form.estimate_id);
  const computedTotal = Math.round(((Number(form.quantity) || 0) * (Number(form.unit_price) || 0)) * 100) / 100;

  const handleBeneficiary = (type, id, name, contactPerson = '') => {
    // Alian: "la compañía toma precedencia por encima del contact person" —
    // BeneficiaryCombobox already resolves `name` to the contact's company
    // when it has one; we keep the individual's name separately so it can
    // still be shown as "Contacto: Jimmy Somers" underneath.
    setForm(f => ({
      ...f, beneficiary_type: type, beneficiary_id: id, beneficiary_name: name,
      beneficiary_contact_person: type === 'contact' && contactPerson && contactPerson !== name ? contactPerson : '',
    }));
    setSimilarWarning(null);
    // Fix P: solo chequea cuando es un beneficiario NUEVO tipeado a mano
    // (type='other' con nombre — nunca al elegir un Worker/Contact ya
    // existente, ni al vaciar el campo).
    if (type === 'other' && name && name.trim()) {
      api.get('/expenses/beneficiaries/similar', { params: { name: name.trim() } })
        .then(res => {
          const match = res.data?.matches?.[0];
          if (match) setSimilarWarning({ typedName: name.trim(), match });
        })
        .catch(() => {});
    }
  };

  const handleTogglePaid = (status) => {
    setForm(f => ({
      ...f, payment_status: status,
      paid_date: status === 'paid' ? (f.paid_date || new Date().toISOString().slice(0, 10)) : f.paid_date,
    }));
  };

  const handleReceiptFile = async (file) => {
    if (!file) return;
    setScanning(true);
    setScanResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/expenses/scan-receipt', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const data = res.data;
      setForm(f => ({
        ...f,
        receipt_file_id: data.receipt_id,
        receipt_url: data.receipt_url,
        // Only prefill fields the user hasn't already typed — never clobber manual entry.
        beneficiary_name: f.beneficiary_name || data.beneficiary_name || f.beneficiary_name,
        amount: f.amount || (data.amount != null ? String(data.amount) : f.amount),
        date: f.date || (data.date || f.date),
      }));
      setScanResult(data.extraction_ok ? 'ok' : 'failed');
      if (data.extraction_ok) toast.success(t('expenses.scanSuccess'));
      else toast.info(t('expenses.scanFailed'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('expenses.scanFailed'));
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    const total = computedTotal;
    if (!total || total <= 0) { toast.error(t('expenses.amountRequired')); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: total,
        quantity: Number(form.quantity) || 1,
        unit_price: Number(form.unit_price) || 0,
        item_index: form.item_index === null || form.item_index === '' ? null : Number(form.item_index),
        item_description: form.item_index !== null && selectedEstimate
          ? (selectedEstimate.items?.[Number(form.item_index)]?.description || '')
          : form.item_description,
        // Fix O: si el metodo es cheque, el numero tipeado en Reference tambien
        // se guarda en check_number (campo viejo, lo siguen leyendo otras partes).
        check_number: form.payment_method === 'check' ? (form.payment_reference || '') : form.check_number,
      };
      if (editing) await api.put(`/expenses/${editing.id}`, payload);
      else await api.post('/expenses', payload);
      toast.success(editing ? t('success.updated') : t('success.created'));
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setSaving(false);
    }
  };

  const receiptImgSrc = form.receipt_url ? `${process.env.REACT_APP_BACKEND_URL}${form.receipt_url}` : '';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow'] text-xl">{editing ? t('expenses.editExpense') : t('expenses.newExpense')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Receipt photo — capture/upload first so vision can pre-fill Beneficiary + Amount below */}
          <div className="space-y-1.5">
            <Label>{t('expenses.receipt')}</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="expense-receipt-input"
              onChange={e => handleReceiptFile(e.target.files?.[0])}
            />
            {receiptImgSrc ? (
              <div className="flex items-center gap-3 p-2 border border-gray-200 rounded-lg bg-gray-50">
                <img src={receiptImgSrc} alt="Receipt" className="w-14 h-14 object-cover rounded-md border border-gray-200" data-testid="expense-receipt-thumbnail" />
                <div className="flex-1 text-xs">
                  {scanning ? (
                    <span className="flex items-center gap-1.5 text-gray-500"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t('expenses.scanningReceipt')}</span>
                  ) : scanResult === 'ok' ? (
                    <span className="flex items-center gap-1.5 text-emerald-600 font-medium"><CheckCircle2 className="w-3.5 h-3.5" /> {t('expenses.scanSuccess')}</span>
                  ) : scanResult === 'failed' ? (
                    <span className="flex items-center gap-1.5 text-amber-600 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> {t('expenses.scanFailed')}</span>
                  ) : null}
                  <button type="button" className="block text-blue-600 hover:underline mt-0.5" onClick={() => fileInputRef.current?.click()} data-testid="expense-receipt-change-btn">
                    {t('expenses.changeReceipt')}
                  </button>
                </div>
                <button type="button" className="text-gray-400 hover:text-red-500" onClick={() => setForm(f => ({ ...f, receipt_file_id: '', receipt_url: '' }))} data-testid="expense-receipt-remove-btn">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button type="button" variant="outline" className="w-full" disabled={scanning} onClick={() => fileInputRef.current?.click()} data-testid="expense-receipt-attach-btn">
                {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
                {scanning ? t('expenses.scanningReceipt') : t('expenses.attachReceipt')}
              </Button>
            )}
          </div>

          {/* Beneficiary FIRST — Alian: "quien es el beneficiario, lo primero de todo" */}
          <div className="space-y-1.5">
            <Label>{t('expenses.beneficiary')} <span className="text-gray-400 font-normal">— {t('expenses.beneficiaryHelper')}</span></Label>
            <BeneficiaryCombobox
              users={users}
              contacts={contacts}
              type={form.beneficiary_type}
              id={form.beneficiary_id}
              name={form.beneficiary_name}
              onChange={handleBeneficiary}
              testId="expense-beneficiary-combobox"
            />
            {form.beneficiary_contact_person && (
              <p className="text-xs text-gray-500" data-testid="expense-beneficiary-contact-person">
                {t('expenses.contactPerson', { name: form.beneficiary_contact_person })}
              </p>
            )}
            {similarWarning && (
              <div className="flex items-start gap-2 p-2.5 rounded-lg border border-amber-200 bg-amber-50" data-testid="expense-similar-beneficiary-warning">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <p className="text-xs text-amber-800">
                    {t('expenses.similarBeneficiaryWarning', {
                      name: similarWarning.match.name, count: similarWarning.match.expenses,
                      defaultValue: 'Already exists "{{name}}" with {{count}} expense(s) recorded.',
                    })}
                  </p>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs border-amber-300"
                      data-testid="expense-similar-beneficiary-use-existing-btn"
                      onClick={() => { setForm(f => ({ ...f, beneficiary_name: similarWarning.match.name })); setSimilarWarning(null); }}>
                      {t('expenses.useExistingBeneficiary', { defaultValue: 'Use the existing one' })}
                    </Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-amber-700"
                      data-testid="expense-similar-beneficiary-create-new-btn"
                      onClick={() => setSimilarWarning(null)}>
                      {t('expenses.createNewAnyway', { defaultValue: 'Create a new one anyway' })}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Project — optional now (Alian: gastos generales como combustible no siempre tienen proyecto) */}
          <div className="space-y-1.5">
            <Label>{t('expenses.projectOptional')}</Label>
            <Select value={form.project_id || '_none'} onValueChange={v => setForm(f => ({ ...f, project_id: v === '_none' ? '' : v, estimate_id: '', item_index: null }))} disabled={!!defaultProjectId}>
              <SelectTrigger data-testid="expense-project-select"><SelectValue placeholder={t('expenses.selectProject')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">{t('expenses.noProjectOption')}</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.project_number} — {p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>{t('expenses.type')}</Label>
            <Select value={form.expense_type} onValueChange={v => setForm(f => ({ ...f, expense_type: v }))}>
              <SelectTrigger data-testid="expense-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map(ty => <SelectItem key={ty} value={ty}>{t(`expenses.types.${ty}`, { defaultValue: ty.charAt(0).toUpperCase() + ty.slice(1) })}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Qty x Unit x Unit Price = Total — Alian: "180 pies x $1.89 = $340.20", "3 dias x $250 = $750" */}
          <div className="space-y-1.5">
            <Label>{t('expenses.amount')} ($) *</Label>
            <div className="grid grid-cols-4 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-400 font-normal">{t('expenses.quantity')}</Label>
                <Input data-testid="expense-quantity-input" type="number" min="0" step="0.01" value={form.quantity}
                  onWheel={e => e.target.blur()} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-400 font-normal">{t('expenses.unit')}</Label>
                <Input data-testid="expense-unit-input" value={form.unit} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} placeholder={t('expenses.unitPlaceholder')} />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-400 font-normal">{t('expenses.unitPrice')}</Label>
                <Input data-testid="expense-unit-price-input" type="number" min="0" step="0.01" value={form.unit_price}
                  onWheel={e => e.target.blur()} onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))} placeholder="0.00" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] text-gray-400 font-normal">{t('expenses.total')}</Label>
                <Input data-testid="expense-total-display" value={computedTotal.toFixed(2)} disabled className="bg-gray-100 font-semibold text-gray-700" />
              </div>
            </div>
          </div>

          {/* Assign to estimate item (optional) */}
          {form.project_id && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t('expenses.estimate')}</Label>
                <Select value={form.estimate_id || 'none'} onValueChange={v => setForm(f => ({ ...f, estimate_id: v === 'none' ? '' : v, item_index: null }))}>
                  <SelectTrigger data-testid="expense-estimate-select"><SelectValue placeholder={t('expenses.selectEstimate')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('expenses.noEstimate')}</SelectItem>
                    {estimates.map(e => <SelectItem key={e.id} value={e.id}>{e.estimate_number}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('expenses.item')}</Label>
                <Select
                  value={form.item_index === null || form.item_index === '' ? 'none' : String(form.item_index)}
                  onValueChange={v => setForm(f => ({ ...f, item_index: v === 'none' ? null : Number(v) }))}
                  disabled={!selectedEstimate}
                >
                  <SelectTrigger data-testid="expense-item-select"><SelectValue placeholder={t('expenses.selectItem')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('expenses.noItem')}</SelectItem>
                    {(selectedEstimate?.items || []).map((it, i) => (
                      <SelectItem key={i} value={String(i)}>
                        {it.sku ? `${it.sku} — ${it.name || `Item ${i + 1}`}` : (it.name || it.description || `Item ${i + 1}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Description + Date */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('expenses.description')}</Label>
              <Input data-testid="expense-description-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('expenses.descriptionPlaceholder')} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('expenses.date')}</Label>
              <Input data-testid="expense-date-input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>

          {/* Payment status — Paid / Unpaid + check number + paid date */}
          <div className="space-y-1.5 p-2.5 rounded-lg border border-gray-200 bg-gray-50/60">
            <Label className="text-xs text-gray-500">{t('expenses.paymentStatus')}</Label>
            <div className="flex gap-2">
              <button type="button" onClick={() => handleTogglePaid('unpaid')} data-testid="expense-status-unpaid-btn"
                className={`flex-1 h-9 rounded-md text-sm font-medium border transition-colors ${form.payment_status !== 'paid' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-300 hover:border-amber-400'}`}>
                {t('expenses.unpaid')}
              </button>
              <button type="button" onClick={() => handleTogglePaid('paid')} data-testid="expense-status-paid-btn"
                className={`flex-1 h-9 rounded-md text-sm font-medium border transition-colors ${form.payment_status === 'paid' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-300 hover:border-emerald-400'}`}>
                {t('expenses.paid')}
              </button>
            </div>
            {form.payment_status === 'paid' && (
              <div className="grid grid-cols-2 gap-3 pt-1.5">
                <div className="space-y-1">
                  <Label className="text-xs">{t('expenses.paymentMethod')}</Label>
                  <Select value={form.payment_method || '_none'} onValueChange={v => setForm(f => ({ ...f, payment_method: v === '_none' ? '' : v }))}>
                    <SelectTrigger data-testid="expense-payment-method-select" className="h-9 bg-white"><SelectValue placeholder={t('expenses.paymentMethodPlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">{t('expenses.paymentMethodNone')}</SelectItem>
                      <SelectItem value="cash">{t('expenses.paymentMethods.cash')}</SelectItem>
                      <SelectItem value="check">{t('expenses.paymentMethods.check')}</SelectItem>
                      <SelectItem value="ach">{t('expenses.paymentMethods.ach')}</SelectItem>
                      <SelectItem value="card">{t('expenses.paymentMethods.card')}</SelectItem>
                      <SelectItem value="other">{t('expenses.paymentMethods.other')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{form.payment_method === 'check' ? t('expenses.checkNumber') : t('expenses.paymentReference')}</Label>
                  <Input data-testid="expense-payment-reference-input" value={form.payment_reference} onChange={e => setForm(f => ({ ...f, payment_reference: e.target.value }))} placeholder={t('expenses.paymentReferencePlaceholder')} className="h-9 bg-white" />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">{t('expenses.paidDate')}</Label>
                  <Input data-testid="expense-paid-date-input" type="date" value={form.paid_date} onChange={e => setForm(f => ({ ...f, paid_date: e.target.value }))} className="h-9 bg-white" />
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>{t('common.notes')}</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
          </div>

          {editing && <ActivityLog entityType="expenses" entityId={editing.id} />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button data-testid="save-expense-btn" onClick={handleSave} disabled={saving} className="bg-[#003366] hover:bg-[#004080] text-white">
            {editing ? t('common.save') : t('expenses.createExpense')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
